import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService, authManager } from '@df/api';
import { AUTH_BYPASS_DEV } from '../../config/featureFlags';

const isDev = process.env.NODE_ENV === 'development';

function logDevError(context, error) {
  if (!isDev) return;
  console.error(context, error);
}

function extractErrorMessage(error) {
  const data = error?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.error?.message) return data.error.message;
  if (data?.detail) return data.detail;
  if (data && typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length > 0) {
      const first = data[keys[0]];
      if (Array.isArray(first) && first.length > 0) return String(first[0]);
      if (first) return String(first);
    }
  }
  return error?.message || 'Unable to load account data.';
}

const Account = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [bypassNoToken, setBypassNoToken] = useState(false);

  const fields = useMemo(() => {
    if (!user) return [];
    const entries = [];
    if (user.email) entries.push({ label: 'Email', value: user.email });
    if (user.id !== undefined && user.id !== null) entries.push({ label: 'User ID', value: String(user.id) });
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    if (fullName) entries.push({ label: 'Name', value: fullName });
    if (user.username) entries.push({ label: 'Username', value: user.username });
    const createdAt = user.created_at || user.date_joined || user.createdAt;
    if (createdAt) entries.push({ label: 'Created', value: String(createdAt) });
    return entries;
  }, [user]);

  const loadUser = async () => {
    setLoading(true);
    setError('');

    const hasAccessToken = !!authManager.getAccessToken();
    if (!hasAccessToken) {
      if (AUTH_BYPASS_DEV) {
        // Dev-only bypass: allow /me to render without a token.
        setBypassNoToken(true);
        setLoading(false);
        return;
      }
      navigate('/login');
      return;
    }
    setBypassNoToken(false);

    try {
      const response = await AuthService.getCurrentUser();
      setUser(response || null);
    } catch (err) {
      logDevError('[Account] Fetch failed', err);
      const status = err?.status;
      if (status === 401) {
        authManager.clearTokens();
        navigate('/login');
        return;
      }
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const refreshToken = authManager.getRefreshToken();
      if (refreshToken) {
        await AuthService.logout(refreshToken);
      }
    } catch (err) {
      logDevError('[Account] Logout failed', err);
    } finally {
      authManager.clearTokens();
      navigate('/login');
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 3, background: 'rgba(255, 255, 255, 0.95)' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: '#2c5aa0' }}>
            Account
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading account details…
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <Box sx={{ mb: 3 }}>
              {bypassNoToken && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Dev auth bypass enabled.</strong> No auth token/session found. Account data is unavailable until backend auth is running.
                </Alert>
              )}
              {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No account details available.
                </Typography>
              ) : (
                fields.map((field) => (
                  <Box key={field.label} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {field.label}
                    </Typography>
                    <Typography variant="body1">{field.value}</Typography>
                  </Box>
                ))
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={loadUser}
              disabled={loading}
              fullWidth
            >
              Retry
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleLogout}
              disabled={loading}
              fullWidth
            >
              Log out
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Account;
